import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiCopy,
  FiInfo,
  FiRefreshCw,
} from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type {
  DataQualityCheck,
  DataQualitySeverity,
  TikTokCampaignRow,
  TikTokDayPoint,
  TikTokDeviceSplit,
  TikTokFunnel,
  TikTokLinkPreset,
  TikTokMetricsResponse,
} from '@/lib/types';

type LandingHitPoint = { date: string; value: number };

const RANGE_OPTIONS = [
  { days: '1', label: '24h' },
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));

const LANDING_BASE = 'https://harborcoparent.com';

function rangeLabel(daysNum: number): string {
  return daysNum === 1 ? '24h' : `${daysNum}d`;
}

function rangeWindowLabel(daysNum: number): string {
  return daysNum === 1 ? '24-hour' : `${daysNum}-day`;
}

function pct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function formatAxisDate(ymd: string, showYear: boolean): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: '2-digit' } : {}),
  });
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5
      ? 1
      : top <= 10
        ? 2
        : top <= 25
          ? 5
          : top <= 50
            ? 10
            : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let i = step; i < count - 1; i += step) indices.push(i);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

/**
 * Fill calendar days for the chart.
 *
 * Stats use a rolling window (`now - N days`), which can touch N+1 UTC
 * calendar days — e.g. 24h from Jul 21 13:00 → Jul 22 13:00 includes both
 * dates. Plotting only "today" drops yesterday's hits and shows 0 while the
 * Landing hits card is non-zero.
 */
function fillLandingHitsDaily(
  volume: TikTokDayPoint[],
  daysNum: number,
): LandingHitPoint[] {
  const byDate = new Map(volume.map((d) => [d.date, d.fingerprints]));
  const today = new Date();
  const end = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const calendarSpan = daysNum + 1;
  const out: LandingHitPoint[] = [];
  for (let i = calendarSpan - 1; i >= 0; i -= 1) {
    const ms = end - i * 86_400_000;
    const key = new Date(ms).toISOString().slice(0, 10);
    out.push({ date: key, value: byDate.get(key) ?? 0 });
  }
  return out;
}

function StatCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'alert';
}) {
  const toneCls =
    tone === 'alert'
      ? 'border-rose-300 bg-rose-50'
      : tone === 'warn'
        ? 'border-amber-300 bg-amber-50'
        : tone === 'good'
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-gray-200 bg-white';
  return (
    <div className={`rounded-lg shadow-sm border p-4 ${toneCls}`}>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

const SEVERITY_STYLE: Record<
  DataQualitySeverity,
  { box: string; icon: JSX.Element; chip: string }
> = {
  alert: {
    box: 'border-rose-200 bg-rose-50',
    icon: <FiAlertTriangle className='h-4 w-4 text-rose-600' aria-hidden />,
    chip: 'bg-rose-100 text-rose-800',
  },
  warn: {
    box: 'border-amber-200 bg-amber-50',
    icon: <FiAlertTriangle className='h-4 w-4 text-amber-600' aria-hidden />,
    chip: 'bg-amber-100 text-amber-900',
  },
  ok: {
    box: 'border-emerald-200 bg-emerald-50',
    icon: <FiCheckCircle className='h-4 w-4 text-emerald-600' aria-hidden />,
    chip: 'bg-emerald-100 text-emerald-800',
  },
  info: {
    box: 'border-gray-200 bg-gray-50',
    icon: <FiInfo className='h-4 w-4 text-gray-500' aria-hidden />,
    chip: 'bg-gray-100 text-gray-700',
  },
};

function DataQualityPanel({ checks }: { checks: DataQualityCheck[] }) {
  return (
    <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
      {checks.map((c) => {
        const s = SEVERITY_STYLE[c.severity];
        return (
          <div
            key={c.key}
            className={`flex items-start gap-3 rounded-lg border p-3 ${s.box}`}
          >
            <div className='mt-0.5 shrink-0'>{s.icon}</div>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center justify-between gap-2'>
                <p className='text-sm font-medium text-gray-900'>{c.label}</p>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${s.chip}`}
                >
                  {c.count.toLocaleString()}
                </span>
              </div>
              <p className='mt-1 text-xs text-gray-600'>{c.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FunnelBar({ funnel }: { funnel: TikTokFunnel }) {
  const steps = [
    { key: 'fingerprints', label: 'Landing hits', value: funnel.fingerprints },
    { key: 'matched', label: 'Matched', value: funnel.matched },
    { key: 'signups', label: 'Signups', value: funnel.signups },
    { key: 'paid', label: 'Paid', value: funnel.paid },
  ] as const;
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className='space-y-3'>
      {steps.map((step, i) => {
        const prev = i === 0 ? step.value : steps[i - 1].value;
        const stepPct = prev > 0 ? (100 * step.value) / prev : 0;
        return (
          <div key={step.key}>
            <div className='mb-1 flex items-baseline justify-between gap-2 text-sm'>
              <span className='font-medium text-gray-800'>{step.label}</span>
              <span className='tabular-nums text-gray-600'>
                {step.value.toLocaleString()}
                {i > 0 ? (
                  <span className='ml-2 text-xs text-gray-400'>
                    {pct(stepPct)} of prior
                  </span>
                ) : null}
              </span>
            </div>
            <div className='h-2.5 overflow-hidden rounded-full bg-gray-100'>
              <div
                className='h-full rounded-full bg-sky-500'
                style={{ width: `${(step.value / max) * 100}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LandingHitsLineChart({
  daily,
  rangeLabelText,
}: {
  daily: LandingHitPoint[];
  rangeLabelText: string;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const lineColor = '#0ea5e9';

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!daily.length || chartWidth <= 0) return null;

    const n = daily.length;
    const padLeft = 44;
    const padRight = 12;
    const padTop = 16;
    const padBottom = 36;
    const width = chartWidth;
    const height = 240;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / n;
    const maxValue = Math.max(...daily.map((d) => d.value));
    const axisMax = niceAxisMax(maxValue);
    const ticks = yAxisTicks(axisMax);
    const labelIndices = new Set(xLabelIndices(n));
    const firstYear = daily[0].date.slice(0, 4);
    const lastYear = daily[n - 1].date.slice(0, 4);
    const showYearOnAxis = firstYear !== lastYear;

    const points = daily.map((d, i) => {
      const cx = padLeft + i * slotW + slotW / 2;
      const cy =
        height - padBottom - (axisMax > 0 ? (d.value / axisMax) * innerH : 0);
      return { ...d, cx, cy, index: i };
    });

    const linePath = points
      .map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.cx} ${point.cy}`)
      .join(' ');

    return {
      n,
      slotW,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      ticks,
      labelIndices,
      showYearOnAxis,
      points,
      linePath,
    };
  }, [daily, chartWidth]);

  if (!daily.length) {
    return (
      <p className='text-sm text-gray-500'>No TikTok traffic in this range.</p>
    );
  }

  if (!layout) {
    return (
      <div
        ref={chartRef}
        className='h-[240px] rounded-md border border-gray-100 bg-gray-50/50'
      />
    );
  }

  const {
    n,
    slotW,
    width,
    height,
    padLeft,
    padTop,
    padBottom,
    innerW,
    innerH,
    axisMax,
    ticks,
    labelIndices,
    showYearOnAxis,
    points,
    linePath,
  } = layout;
  const baselineY = height - padBottom;
  const hoveredPoint =
    hoveredIndex !== null ? layout.points[hoveredIndex] : null;

  return (
    <div className='min-w-0'>
      <div className='mb-3 flex flex-wrap items-baseline justify-between gap-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            Daily landing hits
          </p>
          <p className='text-xs text-gray-500'>
            /tt fingerprint captures per day ({rangeLabelText})
          </p>
        </div>
        <p className='text-xs text-gray-500 tabular-nums'>
          {formatDateLabel(daily[0].date)} –{' '}
          {formatDateLabel(daily[n - 1].date)}
        </p>
      </div>

      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          height={height}
          role='img'
          aria-label='Daily TikTok landing hits line chart'
        >
          {ticks.map((tick) => {
            const y = baselineY - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={padLeft + innerW}
                  y2={y}
                  stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={padLeft - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-500 text-[10px] tabular-nums'
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {n > 1 ? (
            <path
              d={linePath}
              fill='none'
              stroke={lineColor}
              strokeWidth={2.5}
              strokeLinejoin='round'
              strokeLinecap='round'
              className='pointer-events-none'
            />
          ) : null}

          {points.map((point) => {
            const showXLabel = labelIndices.has(point.index);
            const isHovered = hoveredIndex === point.index;
            return (
              <g key={point.date}>
                <rect
                  x={padLeft + point.index * slotW}
                  y={padTop}
                  width={slotW}
                  height={innerH}
                  fill='transparent'
                  className='cursor-pointer'
                  onMouseEnter={() => setHoveredIndex(point.index)}
                />
                <circle
                  cx={point.cx}
                  cy={point.cy}
                  r={isHovered ? 5 : 3.5}
                  fill={isHovered ? lineColor : '#ffffff'}
                  stroke={lineColor}
                  strokeWidth={isHovered ? 0 : 2}
                  className='pointer-events-none'
                  opacity={hoveredIndex === null || isHovered ? 1 : 0.45}
                />
                {showXLabel ? (
                  <text
                    x={point.cx}
                    y={baselineY + 14}
                    textAnchor='middle'
                    className='fill-gray-600 text-[10px] pointer-events-none'
                  >
                    {formatAxisDate(point.date, showYearOnAxis)}
                  </text>
                ) : null}
              </g>
            );
          })}

          {hoveredPoint ? (
            <g pointerEvents='none'>
              <line
                x1={hoveredPoint.cx}
                y1={padTop}
                x2={hoveredPoint.cx}
                y2={baselineY}
                stroke={lineColor}
                strokeOpacity={0.25}
                strokeWidth={1}
              />
              {(() => {
                const label = `${hoveredPoint.value.toLocaleString()} hits`;
                const dateLabel = formatDateLabel(hoveredPoint.date);
                const tooltipW = Math.max(96, label.length * 7 + 24);
                const tooltipH = 40;
                const tooltipX = Math.min(
                  Math.max(hoveredPoint.cx - tooltipW / 2, padLeft),
                  padLeft + innerW - tooltipW,
                );
                const aboveY = hoveredPoint.cy - tooltipH - 12;
                const tooltipY =
                  aboveY >= padTop ? aboveY : hoveredPoint.cy + 12;
                return (
                  <>
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipW}
                      height={tooltipH}
                      rx={6}
                      fill='#111827'
                      fillOpacity={0.92}
                    />
                    <text
                      x={tooltipX + tooltipW / 2}
                      y={tooltipY + 15}
                      textAnchor='middle'
                      className='fill-white text-[10px] font-medium'
                    >
                      {dateLabel}
                    </text>
                    <text
                      x={tooltipX + tooltipW / 2}
                      y={tooltipY + 30}
                      textAnchor='middle'
                      className='fill-white text-[11px] font-semibold tabular-nums'
                    >
                      {label}
                    </text>
                  </>
                );
              })()}
            </g>
          ) : null}
        </svg>
      </div>
      <p className='mt-2 text-[11px] text-gray-400'>
        Hover a point for the exact daily count.
      </p>
    </div>
  );
}

function DeviceBars({ split }: { split: TikTokDeviceSplit }) {
  const total = Math.max(1, split.total);
  const rows = [
    { label: 'iOS', value: split.ios, color: 'bg-sky-500' },
    { label: 'Android', value: split.android, color: 'bg-violet-500' },
    { label: 'Other', value: split.other, color: 'bg-gray-400' },
  ];
  return (
    <div className='space-y-3'>
      {rows.map((r) => (
        <div key={r.label}>
          <div className='mb-1 flex justify-between text-sm'>
            <span className='text-gray-700'>{r.label}</span>
            <span className='tabular-nums text-gray-600'>
              {r.value.toLocaleString()} ({pct((100 * r.value) / total)})
            </span>
          </div>
          <div className='h-2 overflow-hidden rounded-full bg-gray-100'>
            <div
              className={`h-full rounded-full ${r.color}`}
              style={{ width: `${(r.value / total) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function CampaignTable({ rows }: { rows: TikTokCampaignRow[] }) {
  return (
    <div className='overflow-x-auto rounded-md border border-gray-200'>
      <table className='w-full text-sm'>
        <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
          <tr>
            <th className='px-3 py-3 text-left'>Campaign</th>
            <th className='px-3 py-3 text-left'>Content</th>
            <th className='px-3 py-3 text-left'>Medium</th>
            <th className='px-3 py-3 text-right'>Hits</th>
            <th className='px-3 py-3 text-right'>Matched</th>
            <th className='px-3 py-3 text-right'>Signups</th>
            <th className='px-3 py-3 text-right'>Paid</th>
            <th className='px-3 py-3 text-right'>ttclid</th>
            <th className='px-3 py-3 text-right'>Paid %</th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-100'>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={9} className='px-4 py-8 text-center text-gray-400'>
                No TikTok campaigns in this range.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={`${r.campaign}|${r.content}|${r.medium}`}
                className='hover:bg-gray-50'
              >
                <td className='max-w-[160px] truncate px-3 py-2 font-medium text-gray-800'>
                  {r.campaign}
                </td>
                <td className='max-w-[140px] truncate px-3 py-2 text-gray-600'>
                  {r.content}
                </td>
                <td className='px-3 py-2 font-mono text-xs text-gray-600'>
                  {r.medium}
                </td>
                <td className='px-3 py-2 text-right tabular-nums'>{r.fingerprints}</td>
                <td className='px-3 py-2 text-right tabular-nums text-gray-600'>
                  {r.matched}
                  <span className='ml-1 text-xs text-gray-400'>
                    {pct(r.match_pct)}
                  </span>
                </td>
                <td className='px-3 py-2 text-right tabular-nums'>{r.signups}</td>
                <td className='px-3 py-2 text-right tabular-nums text-gray-700'>
                  {r.paid}
                </td>
                <td className='px-3 py-2 text-right tabular-nums text-gray-600'>
                  {r.with_ttclid}
                </td>
                <td className='px-3 py-2 text-right tabular-nums text-gray-600'>
                  {pct(r.paid_pct)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type='button'
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
      className='inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-600 hover:text-gray-900'
    >
      <FiCopy className='h-3 w-3' aria-hidden />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function LinkBuilder({ presets }: { presets: TikTokLinkPreset[] }) {
  const [campaign, setCampaign] = useState('bio_link');
  const [content, setContent] = useState('');
  const [medium, setMedium] = useState('social');

  const built = useMemo(() => {
    const params = new URLSearchParams();
    // /tt already injects defaults; extras override. Still include all for
    // explicit, copy-pasteable links when iterating creatives.
    if (campaign.trim()) params.set('utm_campaign', campaign.trim());
    if (medium.trim()) params.set('utm_medium', medium.trim());
    if (content.trim()) params.set('utm_content', content.trim());
    const qs = params.toString();
    const path = qs ? `/tt?${qs}` : '/tt';
    return { path, url: `${LANDING_BASE}${path}` };
  }, [campaign, content, medium]);

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        <label className='block text-sm'>
          <span className='mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500'>
            utm_campaign
          </span>
          <input
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            className='w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            placeholder='bio_link'
          />
        </label>
        <label className='block text-sm'>
          <span className='mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500'>
            utm_medium
          </span>
          <input
            value={medium}
            onChange={(e) => setMedium(e.target.value)}
            className='w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            placeholder='social or paid'
          />
        </label>
        <label className='block text-sm'>
          <span className='mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500'>
            utm_content
          </span>
          <input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className='w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
            placeholder='video_01 / creative_a'
          />
        </label>
      </div>

      <div className='flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2'>
        <code className='min-w-0 flex-1 break-all text-sm text-gray-800'>
          {built.url}
        </code>
        <CopyButton text={built.url} />
      </div>
      <p className='text-xs text-gray-500'>
        /tt always sets <code className='font-mono'>utm_source=tiktok</code>.
        Override campaign/medium/content per video or ad creative — they show up
        in the campaign table above.
      </p>

      <div className='space-y-2'>
        <p className='text-sm font-medium text-gray-800'>Presets</p>
        {presets.map((p) => (
          <div
            key={p.label}
            className='flex flex-col gap-2 rounded-md border border-gray-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between'
          >
            <div className='min-w-0'>
              <p className='text-sm font-medium text-gray-900'>{p.label}</p>
              <p className='text-xs text-gray-500'>{p.description}</p>
              <code className='mt-1 block break-all text-xs text-gray-600'>
                {p.url}
              </code>
            </div>
            <div className='flex shrink-0 gap-2'>
              <button
                type='button'
                onClick={() => {
                  try {
                    const u = new URL(p.url);
                    setCampaign(u.searchParams.get('utm_campaign') || 'bio_link');
                    setMedium(u.searchParams.get('utm_medium') || 'social');
                    setContent(u.searchParams.get('utm_content') || '');
                  } catch {
                    /* ignore */
                  }
                }}
                className='rounded border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-600 hover:text-gray-900'
              >
                Use
              </button>
              <CopyButton text={p.url} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminTikTokPage() {
  const navigate = useNavigate({ from: '/admin/tiktok' });
  const { days } = Route.useSearch();
  const daysNum = Number(days);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'tiktok-metrics', daysNum],
    queryFn: () =>
      apiJson<TikTokMetricsResponse>(
        getToken,
        `/v1/admin/tiktok/metrics?days=${daysNum}`,
      ),
  });

  const isFetching =
    useIsFetching({ queryKey: ['admin', 'tiktok-metrics', daysNum] }) > 0;

  const onChangeRange = (next: RangeDays) => {
    if (next === days) return;
    navigate({ search: (prev) => ({ ...prev, days: next }) });
  };

  const data = query.data;
  const o = data?.overview;
  const landingHitsDaily = useMemo(
    () => fillLandingHitsDaily(data?.volume_by_day ?? [], daysNum),
    [data?.volume_by_day, daysNum],
  );

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>TikTok</h1>
          <p className='mt-1 text-sm text-gray-500'>
            First-party funnel for{' '}
            <code className='font-mono text-xs'>/tt</code> bio + paid TikTok
            traffic — landing hits, match rate, signups, and paid.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <div className='inline-flex rounded-md border border-gray-200 bg-white p-0.5 shadow-sm'>
            {RANGE_OPTIONS.map((opt) => {
              const active = opt.days === days;
              return (
                <button
                  key={opt.days}
                  type='button'
                  onClick={() => onChangeRange(opt.days)}
                  className={`rounded px-3 py-1.5 text-xs font-semibold ${
                    active
                      ? 'bg-harbor-50 text-primary'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type='button'
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ['admin', 'tiktok-metrics', daysNum],
              })
            }
            className='inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-sm hover:text-gray-900'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden
            />
            Refresh
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <div className='flex items-center justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : query.isError ? (
        <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Failed to load TikTok metrics.
        </div>
      ) : data && o ? (
        <>
          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5'>
            <StatCard
              label={`Landing hits (${rangeLabel(daysNum)})`}
              value={o.fingerprints.toLocaleString()}
              hint={`${o.bio_link_fingerprints} bio · ${o.paid_ad_fingerprints} paid/ttclid`}
            />
            <StatCard
              label='Match rate'
              value={pct(o.match_pct)}
              hint={`${o.matched}/${o.fingerprints} fingerprints → users`}
            />
            <StatCard
              label='Signups'
              value={o.signups.toLocaleString()}
              hint={`${o.ios_signups} iOS · ${o.android_signups} Android`}
            />
            <StatCard
              label='Paid'
              value={o.paid.toLocaleString()}
              hint={`${pct(o.paid_pct)} of TikTok signups`}
              tone={o.paid > 0 ? 'good' : 'default'}
            />
            <StatCard
              label='ttclid coverage'
              value={pct(o.ttclid_fp_pct)}
              hint={`${o.with_ttclid_fingerprints} hits · ${o.with_ttclid_signups} signups`}
              tone={
                o.paid_ad_fingerprints > 0 && o.ttclid_fp_pct < 50
                  ? 'warn'
                  : 'default'
              }
            />
          </div>

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <LandingHitsLineChart
              daily={landingHitsDaily}
              rangeLabelText={rangeLabel(daysNum)}
            />
          </section>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Data-quality flags
            </h2>
            <DataQualityPanel checks={data.data_quality} />
          </section>

          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Funnel ({rangeLabel(daysNum)})
              </h2>
              <FunnelBar funnel={data.funnel} />
            </section>
            <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Device split (landing hits)
              </h2>
              <DeviceBars split={data.device_split} />
            </section>
          </div>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>
              By campaign × content ({rangeLabel(daysNum)})
            </h2>
            <p className='text-sm text-gray-500'>
              Tag each TikTok video/ad with a distinct{' '}
              <code className='font-mono text-xs'>utm_content</code> (via the
              link builder) to compare creatives here.
            </p>
            <CampaignTable rows={data.by_campaign} />
          </section>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>
              /tt link builder
            </h2>
            <LinkBuilder presets={data.link_presets} />
          </section>

          <section className='space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Recent TikTok attributions
            </h2>
            <div className='overflow-x-auto rounded-md border border-gray-200'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-3 text-left'>When</th>
                    <th className='px-4 py-3 text-right'>User</th>
                    <th className='px-4 py-3 text-left'>Platform</th>
                    <th className='px-4 py-3 text-left'>Source</th>
                    <th className='px-4 py-3 text-left'>Campaign</th>
                    <th className='px-4 py-3 text-left'>Content</th>
                    <th className='px-4 py-3 text-center'>ttclid</th>
                    <th className='px-4 py-3 text-center'>Paid</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {data.recent.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className='px-4 py-8 text-center text-gray-400'
                      >
                        No TikTok attributions yet.
                      </td>
                    </tr>
                  ) : (
                    data.recent.map((r) => (
                      <tr
                        key={`${r.user_id}-${r.created_at}`}
                        className='hover:bg-gray-50'
                      >
                        <td className='whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-500'>
                          {r.created_at}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                          {r.user_id}
                        </td>
                        <td className='px-4 py-2 text-gray-700'>{r.platform}</td>
                        <td className='px-4 py-2 font-mono text-xs text-gray-600'>
                          {r.source}
                        </td>
                        <td className='max-w-[140px] truncate px-4 py-2 text-gray-600'>
                          {r.campaign || '—'}
                        </td>
                        <td className='max-w-[140px] truncate px-4 py-2 text-gray-600'>
                          {r.content || '—'}
                        </td>
                        <td className='px-4 py-2 text-center'>
                          {r.has_ttclid ? '✓' : ''}
                        </td>
                        <td className='px-4 py-2 text-center'>
                          {r.paid ? '✓' : ''}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <p className='text-xs text-gray-400'>
            Integrity checks (unmatched ttclid, duplicate fingerprints) are
            all-time; volume, funnel, devices, and campaigns reflect the selected{' '}
            {rangeWindowLabel(daysNum)} window. Landing hit counts may be ~2×
            true visits while the duplicate fingerprint post remains.
          </p>
        </>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/tiktok')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { days: RangeDays } => {
    const rawDays = String(search.days ?? '');
    return {
      days: ALLOWED_DAYS.has(rawDays) ? (rawDays as RangeDays) : '30',
    };
  },
  component: AdminTikTokPage,
});
